
import React from 'react';
import { format } from 'date-fns';
import { EstimateExportData } from '@/utils/estimateExportUtils';
import { ChangeOrderExportData } from '@/utils/changeOrderExportUtils';
import EstimateHeader from './EstimateHeader';
import EstimateDetails from './EstimateDetails';
import EstimateLineItemsTable from './EstimateLineItemsTable';
import EstimateSummaryTable from './EstimateSummaryTable';
import EstimateFooter from './EstimateFooter';
import EstimateBankingInfo from './EstimateBankingInfo';
import ChangeOrderPdfStylePreview from './ChangeOrderPdfStylePreview';
import { useEnhancedUnitResolution } from './hooks/useEnhancedUnitResolution';
import { processDynamicFields } from '@/components/cost-center/document-defaults/dynamicFieldProcessor';
import './pdf-styles.css';

interface EstimatePdfStylePreviewProps {
  data: EstimateExportData | ChangeOrderExportData;
  className?: string;
  scale?: number;
}

const EstimatePdfStylePreview: React.FC<EstimatePdfStylePreviewProps> = ({ 
  data, 
  className = '',
  scale = 0.75 
}) => {
  // Check if this is a change order and route to appropriate component
  if ('isChangeOrder' in data && data.isChangeOrder) {
    return <ChangeOrderPdfStylePreview data={data as ChangeOrderExportData} className={className} scale={scale} />;
  }

  const { estimate, customer, company, categorizedLineItems, subtotal, taxAmount, totalAmount, userProfile, documentDefaults, discountAmount, discountPercentage } = data as EstimateExportData;

  // Enhanced unit resolution with custom units
  const { getUnitSymbolWithCustom } = useEnhancedUnitResolution(company?.id);

  // Pass along VAT registration for summary - use fresh company data to ensure accuracy
  const isVatRegistered = company?.is_vat_registered ?? false;
  const companyVatRate = company?.["VAT Rate"] || 15;

  // Check if we should show single table or categorized view
  const shouldShowSingleTable = categorizedLineItems.length === 1;

  // Check if we should use "All-Inclusive Cost" instead of "Other"
  const shouldUseAllInclusiveCost = categorizedLineItems.length === 1 && 
    categorizedLineItems[0].categoryName === 'Other';
  
  // Transform categories for display if needed
  const displayCategories = categorizedLineItems.map(category => ({
    ...category,
    categoryName: (shouldUseAllInclusiveCost && category.categoryName === 'Other') 
      ? 'All-Inclusive Cost' 
      : category.categoryName
  }));

  // Calculate validity period in days
  const getValidityDays = () => {
    if (estimate.validity_period) return estimate.validity_period;
    if (!estimate.expires_at) return 14;
    const created = new Date(estimate.estimate_date || estimate.created_at);
    const expires = new Date(estimate.expires_at);
    const diffTime = expires.getTime() - created.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 14;
  };

  // Format phone number to xxx xxx xxxx format
  const formatPhoneNumber = (phone: string | undefined): string => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
    }
    return phone;
  };

  // Get dynamic footer text
  const getFooterText = () => {
    if (documentDefaults?.estimate_footer) {
      const today = format(new Date(), 'dd MMMM yyyy');
      const validityDays = getValidityDays();
      
      return processDynamicFields(documentDefaults.estimate_footer, {
        user: {
          first_name: userProfile?.first_name,
          last_name: userProfile?.last_name,
          email: userProfile?.email,
          personal_phone_number: userProfile?.personal_phone_number,
        },
        company: {
          name: company?.name,
          email: company?.email,
          company_phone_number: company?.company_phone_number,
          street_address: company?.street_address,
          suburb: company?.suburb,
          city: company?.city,
          province_state: company?.province_state,
          postal_code: company?.postal_code,
          website: company?.website,
          bank_name: company?.["Bank Name"],
          branch_name: company?.["Branch Name"],
          branch_code: company?.["Branch Code"],
          account_number: company?.["Account Number"],
          account_holder: company?.["Account Holder"],
        },
        customer: {
          name: [customer?.name, customer?.last_name].filter(Boolean).join(' '),
          first_name: customer?.name,
          last_name: customer?.last_name,
          email: customer?.emails?.[0],
          cell_numbers: customer?.cell_numbers,
          street_address: customer?.street_address,
          suburb: customer?.suburb,
          city: customer?.city,
          province_state: customer?.province_state,
          postal_code: customer?.postal_code,
        },
        estimate: {
          site_street_address: estimate.site_street_address,
          site_suburb: estimate.site_suburb,
          site_city: estimate.site_city,
          site_province_state: estimate.site_province_state,
          site_postal_code: estimate.site_postal_code,
          estimate_number: estimate.estimate_number,
          created_date: (() => { 
            try { 
              const d = estimate.estimate_date || estimate.created_at;
              return d ? format(new Date(d), 'dd MMMM yyyy') : ''; 
            } catch { return ''; } 
          })(),
          expires_date: (() => { 
            try { 
              return estimate.expires_at 
                ? format(new Date(estimate.expires_at), 'dd MMMM yyyy') 
                : ''; 
            } catch { return ''; } 
          })(),
          validity_days: validityDays,
        },
        system: {
          today_date: today,
        }
      });
    }
    
    // Default footer text
    const validityDays = getValidityDays();
    let defaultFooter = `Please note that the above estimate is valid for a period of ${validityDays} days from the day of issue. If you have any questions please do not hesitate to let us know.`;
    
    if (company?.company_phone_number) {
      defaultFooter += ` You can call us on ${formatPhoneNumber(company.company_phone_number)}`;
    }
    
    if (company?.email) {
      defaultFooter += ` or email us on ${company.email}`;
    }
    
    if (userProfile?.first_name && userProfile?.personal_phone_number) {
      defaultFooter += ` or contact ${userProfile.first_name} on their cell on ${formatPhoneNumber(userProfile.personal_phone_number)}`;
    }
    
    defaultFooter += '.';
    return defaultFooter;
  };

  // Render page header component (hidden in PDF export, @page CSS handles it)
  const renderPageHeader = () => (
    <div className="page-header-for-pdf" style={{ height: '120px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start' }}>
      {company?.logo_url && (
        <img 
          src={company.logo_url} 
          alt="Company Logo" 
          className="page-logo-for-pdf"
          style={{ 
            maxWidth: '400px', 
            maxHeight: '150px', 
            objectFit: 'contain' 
          }}
        />
      )}
    </div>
  );

  // Render page footer component with dynamic content (hidden in PDF export, @page CSS handles it)
  const renderPageFooter = () => (
    <div className="page-footer-for-pdf" style={{ 
      marginTop: '40px', 
      paddingTop: '20px', 
      borderTop: '1px solid #ccc',
      fontSize: '9px',
      fontStyle: 'italic',
      fontWeight: 'bold',
      color: 'black',
      lineHeight: '1.4'
    }}>
      <div>{getFooterText()}</div>
    </div>
  );

  return (
    <div className={`pdf-preview-wrapper ${className}`}>
      <div className="pdf-page-container">
        <div className="pdf-paper">
          <div className="pdf-margins">
            {/* Header */}
            {renderPageHeader()}

            {/* Main Content */}
            <div className="pdf-content" id="estimate-preview">
              <div style={{ fontSize: '11px', lineHeight: '1.15', marginBottom: '20px' }}>
                <EstimateHeader company={company} userProfile={userProfile} />
                <EstimateDetails estimate={estimate} customer={customer} userProfile={userProfile} company={company} />
              </div>
              
              {/* Line Items Table */}
              <EstimateLineItemsTable 
                categorizedLineItems={displayCategories} 
                showSingleTable={shouldShowSingleTable}
                estimate={estimate}
                taxAmount={taxAmount}
                totalAmount={totalAmount}
                isVatRegistered={isVatRegistered}
                companyVatRate={companyVatRate}
                getUnitSymbol={getUnitSymbolWithCustom}
                discountAmount={discountAmount}
                discountPercentage={discountPercentage}
              />
            
              {/* Summary Table (only show for multi-category estimates) */}
              {!shouldShowSingleTable && (
                <EstimateSummaryTable 
                  categorizedLineItems={displayCategories}
                  estimate={estimate}
                  subtotal={subtotal}
                  taxAmount={taxAmount}
                  totalAmount={totalAmount}
                  isVatRegistered={isVatRegistered}
                  companyVatRate={companyVatRate}
                  discountAmount={discountAmount}
                  discountPercentage={discountPercentage}
                />
              )}

              {/* Banking Info */}
              <EstimateBankingInfo 
                company={company} 
                estimateNumber={estimate.estimate_number} 
              />
              
              {/* Footer */}
              <EstimateFooter 
                estimate={estimate} 
                company={company} 
                userProfile={userProfile} 
                customer={customer}
                documentDefaults={documentDefaults}
              />
            </div>

            {/* Page Footer with dynamic content */}
            {renderPageFooter()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstimatePdfStylePreview;
